// What voice mode says when it cannot go on (BUILD 2 C2-14): a server, capture
// or client code, and its structural reason, map to a key of the `voice`
// namespace. The server sends codes only; the voice-mode mint's 503 reasons
// and the shared 402 go through ui/voice/error-copy.ts like every surface's.
import type { VoiceCopyKey } from '@/i18n/ns/voice';
import { CaptureError, SpeechHttpError } from '@/speech/contracts';
import { voiceErrorCopy } from './error-copy';

export type VoiceIssue = {
  /** The code it came from (server, capture or client). */
  code: string;
  key: VoiceCopyKey;
  /** Placeholder values for the copy (`{seconds}`). */
  values?: Record<string, string | number>;
};

/**
 * voice-structural: error codes (C-15, C2-14 and the capture codes) to copy
 * keys; a code no entry names reads as the generic failure.
 */
const CODE_KEYS: Readonly<Record<string, VoiceCopyKey>> = {
  permission_denied: 'errorPermission',
  no_microphone: 'errorNoMicrophone',
  microphone_busy: 'errorMicrophoneBusy',
  device_lost: 'errorMicrophoneLost',
  insecure_context: 'errorInsecure',
  unsupported: 'errorUnsupported',
  rate_limited: 'errorRateLimited',
  budget_exhausted: 'errorBudget',
  offline: 'errorOffline',
  request_failed: 'errorOffline',
  request_origin_not_allowed: 'errorOriginNotAllowed',
  speech_unavailable: 'errorProviderUnavailable',
  provider_unavailable: 'errorProviderUnavailable',
  tts_unavailable: 'errorTtsUnavailable',
  not_authenticated: 'errorSignIn',
  company_assignment_required: 'errorCompanyRequired',
  replaced: 'replacedElsewhere',
  hidden: 'pausedHidden',
  idle: 'idleExited',
  mic_held_by_meeting: 'voiceRefusedMeeting',
  no_speech: 'noSpeechHeard',
  draft_kept: 'draftKept',
};

const DEFAULT_RETRY_AFTER_SEC = 60;

/** The issue for a code (and the mint's 503 reason). */
export function voiceIssue(
  code: string,
  reason: string | null = null,
  retryAfterSec: number | null = null,
): VoiceIssue {
  const shared = voiceErrorCopy('voice', code, reason);
  const key = shared?.ns === 'voice' ? shared.key : (CODE_KEYS[code] ?? 'errorGeneric');
  return key === 'errorRateLimited'
    ? {
        code,
        key,
        values: { seconds: Math.max(1, Math.ceil(retryAfterSec ?? DEFAULT_RETRY_AFTER_SEC)) },
      }
    : { code, key };
}

/** The issue for anything a voice-mode call can throw. */
export function voiceIssueFor(error: unknown): VoiceIssue {
  if (error instanceof SpeechHttpError)
    return voiceIssue(error.code, error.reason, error.retryAfterSec);
  if (error instanceof CaptureError) return voiceIssue(error.code);
  const code = (error as { code?: unknown } | null)?.code;
  return voiceIssue(typeof code === 'string' ? code : 'internal');
}
